import { useEffect, useState } from "react";

// Holiday events. During one, the site wears that event's look (colors in
// index.css, decorations in App.js, the logo in Header.js) and plays its
// sounds (lib/eventSounds.js), in both light and dark mode, then goes back to
// the usual look by itself. The calendar repeats every year, in the player's
// own time, and nobody can turn an event off. A small script in
// public/index.html picks the event before the page shows, from the same
// calendar.

// Every event with a look and sounds. `logo`: the pictures on the two cards
// next to the name. `decor`: things floating behind the page, and how they
// move (`motion`). `fall`: things falling from the top, like snow. All are
// icon names from components/pics.js.
export const EVENTS = {
  halloween: { name: "Halloween", logo: ["pumpkin", "ghost"], decor: ["bat", "pumpkin", "ghost", "web", "bat", "candy", "pumpkin", "bat", "ghost", "spider"], motion: "drift" },
  christmas: { name: "Christmas", logo: ["tree", "gift"], decor: ["tree", "snowman", "gift", "star", "bell", "sock", "tree", "star", "gift", "bell"], motion: "sway", fall: "snowflake" },
  newyear: { name: "New Year", logo: ["firework", "champagne"], decor: ["sparkle", "firework", "star", "party", "sparkle", "firework", "champagne", "sparkle", "firework", "star"], motion: "twinkle" },
  valentine: { name: "Valentine's Day", logo: ["heart", "invite"], decor: ["heart", "heart", "rose", "invite", "heart", "rose", "heart", "heart", "invite", "heart"], motion: "drift" },
  carnival: { name: "Carnival", logo: ["mask", "party"], decor: ["mask", "party", "balloon", "star", "mask", "balloon", "party", "mask", "star", "balloon"], motion: "drift" },
  ramadan: { name: "Ramadan", logo: ["moon", "lantern"], decor: ["star", "lantern", "sparkle", "star", "lantern", "sparkle", "moon", "star", "sparkle", "lantern"], motion: "twinkle" },
  eidFitr: { name: "Eid al-Fitr", logo: ["moon", "gift"], decor: ["sparkle", "lantern", "gift", "star", "moon", "candy", "sparkle", "balloon", "star", "lantern"], motion: "twinkle" },
  eidAdha: { name: "Eid al-Adha", logo: ["moon", "mosque"], decor: ["sparkle", "lantern", "mosque", "star", "moon", "candy", "sparkle", "balloon", "star", "lantern"], motion: "twinkle" },
  stpatricks: { name: "St Patrick's Day", logo: ["shamrock", "rainbow"], decor: ["shamrock", "shamrock", "rainbow", "shamrock", "coin", "shamrock", "shamrock", "rainbow", "shamrock", "coin"], motion: "drift" },
  easter: { name: "Easter", logo: ["chick", "egg"], decor: ["chick", "flower", "egg", "flower", "chick", "egg", "flower", "egg", "chick", "flower"], motion: "drift" },
  aprilfools: { name: "April Fools'", logo: ["clown", "upside"], decor: ["clown", "balloon", "upside", "clown", "balloon", "upside", "balloon", "cards", "upside", "clown"], motion: "drift" },
  summer: { name: "Summer", logo: ["sun", "palm"], decor: ["sun", "wave", "palm", "watermelon", "icecream", "wave", "palm", "wave", "icecream", "sun"], motion: "drift" },
};

// The events that switch on by themselves, every year: [event, first day,
// last day], as [month, day]. The others can only be previewed for now, from
// the admin page. Keep in step with public/index.html.
export const CALENDAR = [
  ["halloween", [10, 24], [11, 5]],
  ["christmas", [12, 1], [12, 27]],
  ["newyear", [12, 28], [1, 2]],
];

// Which calendar event is on at `date`, or null.
export function eventOn(date = new Date()) {
  const day = (date.getMonth() + 1) * 100 + date.getDate();
  const hit = CALENDAR.find(([, [m1, d1], [m2, d2]]) => {
    const from = m1 * 100 + d1;
    const to = m2 * 100 + d2;
    return from <= to ? day >= from && day <= to : day >= from || day <= to;
  });
  return hit ? hit[0] : null;
}

// The admin page can show an event (or no event, "none") in this browser
// instead of the calendar's, to preview it.
const PREVIEW_KEY = "partycade.eventPreview";
function readPreview() {
  try {
    const p = localStorage.getItem(PREVIEW_KEY);
    return p === "none" || EVENTS[p] ? p : null;
  } catch {
    return null;
  }
}

const pick = () => {
  const p = readPreview();
  return p ? (p === "none" ? null : p) : eventOn();
};
let current = pick();
const listeners = new Set();

function update() {
  current = pick();
  if (typeof document !== "undefined") {
    if (current) document.documentElement.dataset.season = current;
    else delete document.documentElement.dataset.season;
  }
  listeners.forEach((fn) => fn(current));
}
update();

export const currentEvent = () => current;
export const eventPreview = readPreview;

// An event id, "none" for no event, or null to follow the calendar again.
export function setEventPreview(value) {
  try {
    if (value) localStorage.setItem(PREVIEW_KEY, value);
    else localStorage.removeItem(PREVIEW_KEY);
  } catch {}
  update();
}

// A tab left open overnight picks up the next day's event.
if (typeof window !== "undefined") {
  const atMidnight = () => {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    setTimeout(() => {
      update();
      atMidnight();
    }, next - now + 1000);
  };
  atMidnight();
}

// The event showing right now (an id from EVENTS), or null.
export function useEvent() {
  const [value, setValue] = useState(current);
  useEffect(() => {
    listeners.add(setValue);
    return () => listeners.delete(setValue);
  }, []);
  return value;
}
